import { type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { User } from "lucide-react";
import { t } from "@lingui/macro";
import { Trans } from "@lingui/react/macro";
import { useConvexAuth } from "convex/react";

interface HeaderProps {
  navContent?: ReactNode;
  variant?: "default" | "overlay";
}

export default function Header({
  navContent,
  variant = "default",
}: HeaderProps) {
  const { isAuthenticated } = useConvexAuth();
  const accountLabel = t`Cuenta`;
  const isOverlay = variant === "overlay";

  return (
    <header
      className={
        isOverlay
          ? "absolute inset-x-0 top-0 z-50 bg-transparent"
          : "sticky top-0 z-50 border-b border-[var(--color-border)] bg-white"
      }
    >
      <div
        className="flex h-16 w-full items-center gap-4 px-4 sm:px-6"
        data-testid="app-header-inner"
      >
        <Link
          className={`shrink-0 cursor-pointer font-display text-[1.9rem] italic tracking-[0.02em] ${
            isOverlay ? "landing-header-brand" : "text-[var(--color-ink)]"
          }`}
          to="/"
        >
          BodaWeb
        </Link>
        <nav className="ml-auto flex items-center gap-3">
          {navContent}
          {isAuthenticated ? (
            <Link
              aria-label={accountLabel}
              className="inline-flex h-10 w-10 cursor-pointer items-center justify-center border border-[var(--color-border)] text-[var(--color-muted)] transition-colors hover:border-[var(--color-ink)] hover:text-[var(--color-ink)]"
              data-testid="header-account-link"
              title={accountLabel}
              to="/account"
            >
              <User aria-hidden="true" className="h-4 w-4" strokeWidth={1.8} />
            </Link>
          ) : (
            <Link
              className="inline-flex cursor-pointer items-center justify-center border-b border-transparent px-1 py-2 text-sm font-medium text-[var(--color-ink)] transition-colors hover:border-[var(--color-ink)]"
              search={{
                entry: undefined,
                flow: undefined,
                template: undefined,
              }}
              to="/auth/signin"
            >
              <Trans>Iniciar Sesión</Trans>
            </Link>
          )}
        </nav>
      </div>
    </header>
  );
}
